// Enlaces de un sitio público, en un solo lugar. El mismo sitio vive en dos
// direcciones:
//   - en la app: `/s/<slug>` y `/s/<slug>/<página>`;
//   - en su subdominio (`<slug>.<dominio de sitios>`): `/` y `/<página>`.
// Una página de detalle lleva además la dirección del registro:
// `/<página>/<registro>`. Lo elegido en los selectores va en `?picks=` y la
// búsqueda, los filtros y el orden de las listas en `?q=`, `?<campo>=` y
// `?orden=`, en los dos casos.

import { normalizeBaseDomain } from "./host";

export interface SitePathInput {
  slug: string;
  /** Sin página: la primera del sitio. */
  page?: string;
  /** La dirección del registro en una página de detalle. */
  item?: string;
  /** `picks` ya serializado (components/sites/picks.ts). */
  picks?: string;
  /** Búsqueda, filtros y orden de las listas (`q`, `<campo>`, `orden`), en ese orden. */
  params?: Record<string, string>;
  /** `true` en el subdominio del sitio; `false` en la app. */
  onSiteHost: boolean;
}

/** Ruta (sin origen) de una página del sitio. */
export function sitePagePath({ slug, page, item, picks, params, onSiteHost }: SitePathInput): string {
  const prefix = onSiteHost ? "" : `/s/${encodeURIComponent(slug)}`;
  const withPage = page ? `${prefix}/${encodeURIComponent(page)}` : prefix;
  const path = (page && item ? `${withPage}/${encodeURIComponent(item)}` : withPage) || "/";
  const query = [
    ...(picks ? [`picks=${encodeURIComponent(picks)}`] : []),
    ...Object.entries(params ?? {})
      .filter(([k, v]) => k !== "picks" && v !== "")
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`),
  ];
  return query.length ? `${path}?${query.join("&")}` : path;
}

/** Dirección del sitio dentro de la app: `/s/<slug>`. */
export const siteAppPath = (slug: string): string => sitePagePath({ slug, onSiteHost: false });

/**
 * URL del sitio en su subdominio (`https://<slug>.<base>`), o `null` si no
 * hay dominio de sitios configurado.
 */
export function siteSubdomainUrl(slug: string, baseDomain: string | undefined | null): string | null {
  const base = normalizeBaseDomain(baseDomain);
  return base ? `https://${slug}.${base}` : null;
}

/**
 * `true` si `pathname` es un sitio público dentro de la app (`/s/<slug>`,
 * `/s/<slug>/<página>`). Ahí no va nada del ERP: ni la pantalla de licencia,
 * ni el service worker ni el manifiesto de la PWA (DevTools y el asistente
 * viven en el layout `_authenticated`, que estas rutas no usan).
 */
export function isPublicSitePath(pathname: string): boolean {
  return /^\/s\/[^/]+(\/|$)/.test(pathname);
}

/**
 * La dirección del sitio de `/s/<slug>` o `/s/<slug>/<página>` (decodificada),
 * o `null` si la ruta no es de un sitio. Con ella app/main.tsx carga solo el
 * sitio, sin el ERP.
 */
export function publicSiteSlugFromPath(pathname: string): string | null {
  const m = /^\/s\/([^/]+)(?:\/.*)?$/.exec(pathname);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]) || null;
  } catch {
    return null;
  }
}
